import { useId } from "react";
import { cn } from "../../lib/cn";

/** A time of day on the 24-hour clock. */
export interface TimeOfDay {
  readonly hours: number;
  readonly minutes: number;
}

export interface TimeSelectionProps {
  readonly value: TimeOfDay;
  readonly onChange: (value: TimeOfDay) => void;
  /** The row's caption, e.g. "Time". */
  readonly label: string;
  readonly hoursLabel: string;
  readonly minutesLabel: string;
  readonly amLabel: string;
  readonly pmLabel: string;
}

const TIME_INPUT =
  "size-10 shrink-0 rounded-lg bg-transparent text-center text-body-m-b text-calendar-time-text outline-none focus-visible:ring-1 focus-visible:ring-primary";

/** Typing over a two-digit box: keep the last two digits typed, clamped. */
function parseTyped(raw: string, min: number, max: number): number {
  const digits = raw.replace(/\D/g, "").slice(-2);
  const parsed = Number.parseInt(digits, 10);
  if (Number.isNaN(parsed)) return min;
  return Math.min(max, Math.max(min, parsed));
}

const twoDigits = (value: number) => String(value).padStart(2, "0");

/**
 * The picker's "Time Selection" slot (Figma Date & Time pickers 2818:116890,
 * shown in 3480:65695): a caption over a clock box -- hours : minutes, then
 * the AM/PM toggle at the end. Hours show on the 12-hour clock; the value is
 * 24-hour. Sits inside a CalendarPanel, under the Calendar.
 */
export function TimeSelection({
  value,
  onChange,
  label,
  hoursLabel,
  minutesLabel,
  amLabel,
  pmLabel,
}: TimeSelectionProps) {
  const labelId = useId();
  const pm = value.hours >= 12;
  const hour12 = value.hours % 12 === 0 ? 12 : value.hours % 12;

  const setHour12 = (next: number) =>
    onChange({ ...value, hours: (next % 12) + (pm ? 12 : 0) });
  const setPm = (nextPm: boolean) =>
    onChange({ ...value, hours: (value.hours % 12) + (nextPm ? 12 : 0) });

  return (
    <div className="flex flex-col gap-1 p-2">
      <p
        id={labelId}
        className="text-start text-body-s-r text-calendar-time-text"
      >
        {label}
      </p>
      <div
        role="group"
        aria-labelledby={labelId}
        className="flex h-12 items-center overflow-hidden rounded-lg border border-calendar-time-border bg-container-default-background"
      >
        {/* Digits read hours-then-minutes left to right in both languages;
            in Arabic the toggle lands on the left, as designed. */}
        <div dir="ltr" className="flex min-w-0 flex-1 items-center">
          <input
            type="text"
            inputMode="numeric"
            aria-label={hoursLabel}
            value={twoDigits(hour12)}
            onChange={(event) =>
              setHour12(parseTyped(event.target.value, 1, 12))
            }
            className={TIME_INPUT}
          />
          <span
            aria-hidden="true"
            className="flex-1 text-center text-body-m-b text-calendar-time-text"
          >
            :
          </span>
          <input
            type="text"
            inputMode="numeric"
            aria-label={minutesLabel}
            value={twoDigits(value.minutes)}
            onChange={(event) =>
              onChange({
                ...value,
                minutes: parseTyped(event.target.value, 0, 59),
              })
            }
            className={TIME_INPUT}
          />
        </div>
        <div
          role="radiogroup"
          aria-label={label}
          className="flex h-full w-10 shrink-0 flex-col"
        >
          {([false, true] as const).map((isPm) => (
            <button
              key={String(isPm)}
              type="button"
              role="radio"
              aria-checked={pm === isPm}
              onClick={() => setPm(isPm)}
              className={cn(
                "flex flex-1 items-center justify-center text-caption-s font-bold focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary",
                pm === isPm
                  ? "bg-primary text-neutral-white"
                  : "bg-container-default-background text-calendar-day-text hover:bg-container-hover",
              )}
            >
              {isPm ? pmLabel : amLabel}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
